import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Hardcoded fallback translations (used if DB fetch fails)
const fallbackTranslations = {
  en: {
    home: 'Home',
    about: 'About',
    gallery: 'Gallery',
    links: 'Links',
    news: 'News',
    heroTitle: 'DJ Ole Helledie',
    heroSubtitle: 'DJ & Event Curator',
    exploreMusic: 'Visual vibes',
    discoverMore: 'Discover More',
    servicesTitle: 'Creating Musical Experiences',
    servicesSubtitle: "From intimate celebrations to grand gatherings, every event deserves a soundtrack that reflects its unique spirit",
    eventCurationTitle: 'Event Curation',
    eventCurationBody: 'Crafting the perfect atmosphere for weddings, corporate events, private parties, and cultural celebrations',
    crowdReadingTitle: 'Crowd Reading',
    crowdReadingBody: "Reading the room's energy and responding with music that moves both body and soul, creating shared moments",
    professionalServiceTitle: 'Professional Service',
    professionalServiceBody: 'Reliable, well-prepared, and dedicated to making every event memorable through thoughtful music curation',
    splitEveryEventTitle: 'Every Event Has Its Story',
    splitEveryEventBody: 'DJ Ole Helledie brings together the art of musical curation with the craft of reading a room. Through years of experience behind the decks, he has developed an intuitive understanding of how music can transform spaces and connect people.',
    getInTouch: 'Get in Touch',
    learnMore: 'Learn More',
    aboutTitle: 'About Ole',
    aboutBio: 'I am a DJ with many years of experience from corporate parties, weddings and private events. My focus is simple: to help create the best possible celebration. I play dance friendly pop, funk, soul, disco and the hits that get people on the floor and in a great mood.\nI read the room and the crowd, adjust the music along the way and make sure the energy builds throughout the night. It is about good vibes, timing and giving guests an experience they will remember.',
    musicalVersatilityTitle: 'Musical style',
    musicalVersatilityBody: 'Pop, funk, soul, disco and dance hits tailored to the crowd and the mood of the night.',
    eventExperienceTitle: 'Event Experience',
    eventExperienceBody: 'Experience from events of all sizes: weddings, corporate parties, birthdays, receptions and larger gatherings.',
    professionalApproachTitle: 'Professional Approach',
    professionalApproachBody: 'Easy to work with, always prepared and fully focused on giving hosts and guests a great, stress free experience.',
    musicAndMomentsTitle: 'DJ for corporate parties, weddings and private events',
    musicAndMomentsQuote: 'With a focus on atmosphere, timing and a packed dance floor',
    celebrationsWeddingsTitle: 'Celebrations & Weddings',
    celebrationsWeddingsBody: "Creating the perfect atmosphere for life's most precious moments, from ceremony elegance to reception energy.",
    corporateCulturalTitle: 'Corporate & Cultural Events',
    corporateCulturalBody: 'Professional event curation for conferences, launches, exhibitions, and cultural celebrations.',
    contactCtaTitle: "Let's Create Your Event's Soundtrack",
    contactCtaBody: "Available for events throughout Denmark and beyond. Let's discuss how we can make your celebration unforgettable.",
    viewGallery: 'View Gallery',
    galleryTitle: 'Visual Stories',
    gallerySubtitle: 'Moments captured in light and sound',
    linksTitle: 'Artistic Circle',
    motherArt: 'Lene Helledie Art',
    daughterDj: 'Daughter\'s Musical Journey',
    motherArtDesc: 'A lifetime dedicated to visual storytelling and creative expression',
    daughterDjDesc: 'The evolution continues with fresh perspectives and boundless creativity',
    artConnectsTitle: 'Art Connects Us All',
    artConnectsBody: 'In our family, creativity is the common thread that weaves through generations. Each of us expresses the same universal truths through different mediums - visual art, musical landscapes, and sonic storytelling.',
    explore: 'Explore',
    contactInfo: 'Get in Touch',
    connectWith: 'Available for events and collaborations',
    followMe: 'Follow the Journey',
    allRightsReserved: 'All rights reserved',
    brandTagline: 'DJ & Event Curator',
    brandDescription: 'Creating memorable experiences through thoughtful music curation and professional event DJing.',
    connectExplore: 'Connect with me to explore how we can make your event special',
  },
  da: {
    home: 'Hjem',
    about: 'Om Ole',
    gallery: 'Galleri',
    links: 'Links',
    news: 'Nyheder',
    heroTitle: 'DJ Ole Helledie',
    heroSubtitle: 'DJ & Eventkurator',
    exploreMusic: 'Visuelle øjeblikke',
    discoverMore: 'Oplev mere',
    servicesTitle: 'Skaber musikalske oplevelser',
    servicesSubtitle: 'Fra intime fejringer til store begivenheder – hvert event fortjener et soundtrack, der afspejler dets unikke ånd',
    eventCurationTitle: 'Eventkuratering',
    eventCurationBody: 'Skaber den perfekte atmosfære til bryllupper, firmaevents, private fester og kulturelle begivenheder',
    crowdReadingTitle: 'At læse publikum',
    crowdReadingBody: 'At aflæse rummets energi og svare igen med musik, der bevæger både krop og sjæl – og skaber fælles øjeblikke',
    professionalServiceTitle: 'Professionel service',
    professionalServiceBody: 'Pålidelig, velforberedt og dedikeret til at gøre hvert event mindeværdigt gennem omtænksom musikalsk kuratering',
    splitEveryEventTitle: 'Hvert event har sin historie',
    splitEveryEventBody: 'DJ Ole Helledie forener kunstnerisk musikkuratering med håndværket at læse et rum. Gennem års erfaring bag pulten har han udviklet en intuitiv forståelse af, hvordan musik kan forandre rum og forbinde mennesker.',
    getInTouch: 'Kom i kontakt',
    learnMore: 'Læs mere',
    aboutTitle: 'Om Ole',
    aboutBio: 'Jeg er DJ med mange års erfaring fra firmafester, bryllupper og private arrangementer. Mit fokus er enkelt: at hjælpe med at skabe den bedst mulige fest. Jeg spiller dansevenlig pop, funk, soul, disco og de hits, der får folk ud på gulvet og i godt humør.\nJeg læser rummet og publikum, tilpasser musikken undervejs og sørger for, at energien bygger sig op gennem aftenen. Det handler om god stemning, timing og at give gæsterne en oplevelse, de husker.',
    musicalVersatilityTitle: 'Musikalsk stil',
    musicalVersatilityBody: 'Pop, funk, soul, disco og dansehits – tilpasset publikum og stemningen på aftenen.',
    eventExperienceTitle: 'Eventerfaring',
    eventExperienceBody: 'Erfaring fra store og små events: bryllupper, firmafester, fødselsdage, receptioner og større arrangementer.',
    professionalApproachTitle: 'Professionel tilgang',
    professionalApproachBody: 'Nem at arbejde med, altid forberedt og med fuldt fokus på, at værter og gæster får en god oplevelse uden stress.',
    musicAndMomentsTitle: 'DJ til firmafester, bryllupper og private fester',
    musicAndMomentsQuote: 'Med fokus på stemning, timing og et fyldt dansegulv',
    celebrationsWeddingsTitle: 'Fester & bryllupper',
    celebrationsWeddingsBody: 'Skaber den perfekte atmosfære til livets mest dyrebare øjeblikke – fra ceremoniens elegance til receptionens energi.',
    corporateCulturalTitle: 'Firma- & kulturevents',
    corporateCulturalBody: 'Professionel eventkuratering til konferencer, lanceringer, udstillinger og kulturelle begivenheder.',
    contactCtaTitle: 'Lad os skabe soundtracket til dit event',
    contactCtaBody: 'Tilgængelig til events i hele Danmark og derudover. Lad os tale om, hvordan vi gør din fejring uforglemmelig.',
    viewGallery: 'Se Galleri',
    galleryTitle: 'Visuelle fortællinger',
    gallerySubtitle: 'Øjeblikke fanget i lys og lyd',
    linksTitle: 'Kunstnerisk kreds',
    motherArt: 'Lene Helledie kunst',
    daughterDj: 'Datterens musikalske rejse',
    motherArtDesc: 'Et liv dedikeret til visuel fortælling og kreativt udtryk',
    daughterDjDesc: 'Evolutionen fortsætter med friske perspektiver og grænseløs kreativitet',
    artConnectsTitle: 'Kunst forbinder os alle',
    artConnectsBody: 'I vores familie er kreativiteten den røde tråd, der væver sig gennem generationer. Vi udtrykker de samme universelle sandheder gennem forskellige medier – visuel kunst, musikalske landskaber og lydlige fortællinger.',
    explore: 'Udforsk',
    contactInfo: 'Kom i kontakt',
    connectWith: 'Tilgængelig for begivenheder og samarbejder',
    followMe: 'Følg rejsen',
    allRightsReserved: 'Alle rettigheder forbeholdes',
    brandTagline: 'DJ & Eventkurator',
    brandDescription: 'Skaber mindeværdige oplevelser gennem omtænksom musikkuratering og professionel event-DJing.',
    connectExplore: 'Tag kontakt for at udforske, hvordan vi kan gøre dit event helt særligt',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('da');
  const [dbContent, setDbContent] = useState({});

  useEffect(() => {
    supabase
      .from('site_content')
      .select('key, value_da, value_en')
      .then(({ data, error }) => {
        if (error || !data) return;
        const map = {};
        data.forEach(row => {
          map[row.key] = { da: row.value_da, en: row.value_en };
        });
        setDbContent(map);
      });
  }, []);

  const t = (key) => {
    // DB content takes priority over hardcoded fallback
    if (dbContent[key]) {
      return dbContent[key][language] || fallbackTranslations[language]?.[key] || key;
    }
    return fallbackTranslations[language]?.[key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'da' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
